<template>
  <modal
    :id="modalId"
    ref="modal"
    @close="close"
    :enable-mask="true"
    wrapperSize="flex"
    size="flex"
    :title="fileTitle"
    :is-closeable="true"
    :class="['file-preview-modal', { 'pdf-modal': isPDF, 'excel-modal': isExcel }]"
    :show-footer="false"
  >
    <template v-slot:body>
      <div class="file-preview__container">
        <!-- PDF Preview -->
        <div v-if="isPDF" class="pdf-preview h-full">
          <!-- Loader -->
          <div v-if="isLoading" class="loader-container">
            <LoadingNew/>
          </div>
          <iframe
            :src="fileUrl"
            type="application/pdf"
            width="100%"
            :height="dynamicHeight"
            frameborder="0"
            @load="handleIframeLoad"
            :class="{ 'opacity-0': isLoading }"
          ></iframe>
        </div>

        <!-- Image Preview -->
        <div v-else-if="isImage" class="image-preview relative w-[800px] max-w-[800px]">
          <div class="absolute z-2 bg-transparent w-full h-full"></div>
          <img
            :src="fileUrl"
            :alt="fileName"
            class="preview-image"
          />
        </div>

        <!-- Audio Preview -->
        <div v-else-if="isAudio" class="audio-preview">
          <div class="text-center py-8">
            <Icon name="musical-note" class="w-16 h-16 text-blue-500 mx-auto mb-4"/>
            <h3 class="text-lg font-medium text-gray-900 mb-4">{{ fileName }}</h3>
            <audio
              :src="fileUrl"
              controls
              preload="metadata"
              class="w-full max-w-md mx-auto"
            >
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>

        <!-- Video Preview -->
        <div v-else-if="isVideo" class="video-preview">
          <div class="text-center">
            <video
              :src="fileUrl"
              controls
              preload="metadata"
              class="w-full max-w-4xl max-h-96 mx-auto"
            >
              Your browser does not support the video element.
            </video>
          </div>
        </div>

        <!-- Excel Preview -->
        <div v-else-if="isExcel" class="excel-preview">
          <!-- Loader -->
          <div v-if="isLoading" class="loader-container">
            <LoadingNew/>
          </div>

          <!-- Excel Content -->
          <div v-if="!excelViewerError && excelData" class="excel-content">
            <!-- Worksheet Tabs -->
            <div v-if="excelData.SheetNames && excelData.SheetNames.length > 1" class="excel-tabs border-b">
              <div class="flex space-x-1 overflow-x-auto">
                <button
                  v-for="sheetName in excelData.SheetNames"
                  :key="sheetName"
                  @click="activeSheet = sheetName"
                  :class="[
                    'px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeSheet === sheetName
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  ]"
                >
                  {{ sheetName }}
                </button>
              </div>
            </div>

            <!-- Spreadsheet Table -->
            <div class="excel-table-container overflow-auto">
              <table class="excel-table border-collapse border border-gray-300">
                <tbody>
                <tr v-for="(row, rowIndex) in currentSheetData" :key="rowIndex" class="border border-gray-200">
                  <td
                    v-for="(cell, colIndex) in row"
                    :key="colIndex"
                    class="border border-gray-300 px-2 py-1 text-sm min-w-24 max-w-48 truncate"
                    :title="cell"
                  >
                    {{ cell }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Error/Fallback -->
          <div v-else-if="excelViewerError" class="excel-fallback text-center py-12">
            <Icon name="file-spreadsheet" class="w-16 h-16 text-red-500 mx-auto mb-4"/>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Error Loading Excel File</h3>
            <p class="text-gray-500 mb-6">{{ fileName }}</p>
            <p class="text-sm text-gray-400 mb-6">{{ excelErrorMessage }}</p>

            <div class="space-y-3">
              <a
                :href="fileUrl"
                target="_blank"
                class="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Icon name="external-link-alt" size="14" class="w-4 h-4 mr-2"/>
                Open in New Tab
              </a>

              <div>
                <a
                  :href="fileUrl"
                  :download="fileName"
                  class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Icon name="download" size="14" class="w-4 h-4 mr-2"/>
                  Download Excel File
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Text/Document Preview -->
        <div v-else-if="isTextDocument" class="document-preview">
          <!-- Loader -->
          <div v-if="isLoading" class="loader-container">
            <LoadingNew/>
          </div>
          <iframe
            :src="fileUrl"
            width="100%"
            height="600px"
            frameborder="0"
            @load="handleIframeLoad"
            :class="{ 'opacity-0': isLoading }"
          ></iframe>
        </div>

        <!-- Unsupported File Type -->
        <div v-else class="unsupported-preview">
          <div class="text-center py-12">
            <Icon name="document" class="w-16 h-16 text-gray-400 mx-auto mb-4"/>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Preview not available</h3>
            <p class="text-gray-500 mb-6">This file type cannot be previewed in the browser.</p>

            <div class="space-y-3">
              <a
                :href="fileUrl"
                target="_blank"
                class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Icon name="external-link-alt" size="14" class="w-2 h-2 mr-2"/>
                Open in New Tab
              </a>

              <div>
                <a
                  v-if="!isLink"
                  :href="fileUrl"
                  :download="fileName"
                  class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Icon name="download" size="14" class="w-4 h-4 mr-2"/>
                  Download File
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Download Button -->
        <div v-if="!isLink" class="download-button-container">
          <a
            v-if="isImage"
            :href="`/api/files/${fileId}/download?url=${encodeURIComponent(fileUrl)}`"
            class="download-button button"
            :title="`Download ${fileName}`"
            target="_blank"
          >
            <Icon name="download" size="14"/>
            <span class="button__label">Download</span>
          </a>
          <a
            v-else
            :href="fileUrl"
            :download="isImage ? '' : fileName"
            class="download-button button"
            :title="`Download ${fileName}`"
          >
            <Icon name="download" size="14"/>
            <span class="button__label">Download</span>
          </a>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
  import Modal from './Modal.vue'
  import Icon from '../Icon/Icon.vue'
  import MODAL_IDS from '../../constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'

  export default {
    name: 'FilePreviewModal',
    components: {
      LoadingNew,
      Modal,
      Icon
    },
    data () {
      return {
        modalId: MODAL_IDS.FILE_PREVIEW,
        isLoading: true,
        excelViewerError: false,
        excelErrorMessage: '',
        excelData: null,
        activeSheet: null,
        windowHeight: window.innerHeight
      }
    },
    computed: {
      modalContext () {
        return this.$store.state.ui.modalContext
      },
      fileId () {
        return this.modalContext?.fileId || ''
      },
      fileUrl () {
        return this.modalContext?.fileUrl || ''
      },
      fileName () {
        return this.modalContext?.fileName || 'Unknown File'
      },
      fileType () {
        return this.modalContext?.fileType || ''
      },
      isLink () {
        return this.fileType === 'link' ||
          this.fileType === 'video_link' ||
          this.fileType === 'url' ||
          this.fileType.includes('url') ||
          this.fileType.includes('link') ||
          this.fileType.includes('unknown')
      },
      fileTitle () {
        return this.fileName || 'File Preview'
      },
      isPDF () {
        return this.fileType === 'pdf' || this.fileUrl.includes('.pdf')
      },
      isImage () {
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
        return imageTypes.includes(this.fileType.toLowerCase()) ||
          imageTypes.some(type => this.fileUrl.toLowerCase().includes(`.${type}`))
      },
      isAudio () {
        const audioTypes = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac', 'audio/mpeg']
        console.log(this.fileType)
        return audioTypes.includes(this.fileType.toLowerCase()) ||
          audioTypes.some(type => this.fileUrl.toLowerCase().includes(`.${type}`))
      },
      isVideo () {
        const videoTypes = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v']
        return videoTypes.includes(this.fileType.toLowerCase()) ||
          videoTypes.some(type => this.fileUrl.toLowerCase().includes(`.${type}`))
      },
      isExcel () {
        const excelTypes = ['xlsx', 'xls', 'xlsm', 'xlsb', 'xltx', 'xltm', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
        return excelTypes.includes(this.fileType.toLowerCase()) ||
          excelTypes.some(type => this.fileUrl.toLowerCase().includes(`.${type.replace('application/vnd.', '').replace('officedocument.spreadsheetml.sheet', 'xlsx').replace('ms-excel', 'xls')}`))
      },
      isTextDocument () {
        const textTypes = ['txt', 'md', 'html', 'css', 'js', 'json', 'xml']
        return textTypes.includes(this.fileType.toLowerCase()) ||
          textTypes.some(type => this.fileUrl.toLowerCase().includes(`.${type}`))
      },
      currentSheetData () {
        if (!this.excelData || !this.activeSheet) return []

        const worksheet = this.excelData.Sheets[this.activeSheet]
        if (!worksheet) return []

        // Convert worksheet to array of arrays
        return this.worksheetToArray(worksheet)
      },
      dynamicHeight () {
        // Use 80% of viewport height minus space for header, padding, and download button
        const availableHeight = Math.max(400, this.windowHeight * 0.8 - 210)
        return `${availableHeight}px`
      }
    },
    methods: {
      close () {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.FILE_PREVIEW)
        this.$emit('close')
      },
      handleIframeLoad () {
        this.isLoading = false
      },
      async loadExcelFile () {
        if (!this.isExcel || !this.fileUrl) return

        try {
          this.isLoading = true
          this.excelViewerError = false
          this.excelErrorMessage = ''

          // Dynamic import of xlsx library
          const XLSX = await this.loadXLSXLibrary()

          // Fetch the Excel file
          const response = await fetch(this.fileUrl)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const arrayBuffer = await response.arrayBuffer()

          // Parse the Excel file
          const workbook = XLSX.read(arrayBuffer, { type: 'array' })

          this.excelData = workbook
          this.activeSheet = workbook.SheetNames[0] // Set first sheet as active
          this.isLoading = false

        } catch (error) {
          console.error('Error loading Excel file:', error)
          this.excelViewerError = true
          this.excelErrorMessage = 'Failed to load and parse Excel file. ' + (error.message || '')
          this.isLoading = false
        }
      },
      async loadXLSXLibrary () {
        // Check if XLSX is already loaded
        if (window.XLSX) {
          return window.XLSX
        }

        // Dynamically load XLSX library from CDN
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
          script.onload = () => {
            if (window.XLSX) {
              resolve(window.XLSX)
            } else {
              reject(new Error('XLSX library failed to load'))
            }
          }
          script.onerror = () => reject(new Error('Failed to load XLSX library'))
          document.head.appendChild(script)
        })
      },
      worksheetToArray (worksheet) {
        if (!worksheet || !worksheet['!ref']) return []

        const range = window.XLSX.utils.decode_range(worksheet['!ref'])
        const rows = []

        for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
          const row = []
          for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
            const cellAddress = window.XLSX.utils.encode_cell({ r: rowNum, c: colNum })
            const cell = worksheet[cellAddress]
            row.push(cell ? cell.v : '')
          }
          rows.push(row)
        }

        return rows
      },
      handleResize () {
        this.windowHeight = window.innerHeight
      }
    },
    mounted () {
      window.addEventListener('resize', this.handleResize)
    },
    beforeUnmount () {
      window.removeEventListener('resize', this.handleResize)
    },
    watch: {
      modalContext: {
        handler (newContext) {
          if (newContext && newContext.fileUrl) {
            this.isLoading = true
            this.excelViewerError = false
            this.excelErrorMessage = ''
            this.excelData = null
            this.activeSheet = null

            // If it's an Excel file, load it
            if (this.isExcel) {
              this.loadExcelFile()
            }
          }
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  .file-preview__container {
    display: flex;
    flex-direction: column;
    position: relative;

    .download-button-container {
      margin-top: 0.5rem;
      z-index: 20;
      display: flex;
      justify-content: flex-end;

      .download-button {
        margin-right: 0;
        background-color: var(--primary-community);
        border-color: var(--primary-community);
        color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(8px);

        &:hover {
          background-color: var(--primary-community-darker);
          border-color: var(--primary-community-darker);
          color: white;
          text-decoration: none;
        }

        &:focus {
          box-shadow: 0 0 0 2px var(--primary-community);
        }

        .svg-icon {
          left: 6px;
        }

        .button__label {
          @media (max-width: 640px) {
            display: none;
          }
        }

        @media (max-width: 640px) {
          padding-left: 8px;
          padding-right: 8px;
          min-width: 32px;
        }
      }
    }

    .pdf-preview,
    .excel-preview,
    .document-preview {
      width: 100%;
      max-width: 80vw;
      max-height: 100%;
      position: relative;

      .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.9);
        z-index: 10;

        .loader {
          text-align: center;
        }
      }

      iframe {
        transition: opacity 0.3s ease;
      }
    }

    .image-preview {
      display: flex;
      justify-content: center;

      .preview-image {
        max-width: 800px;
        object-fit: contain;
        border-radius: 8px;
      }
    }

    .audio-preview {
      width: 100%;

      audio {
        height: 40px;
        border-radius: 25px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }

    .video-preview {
      width: 100%;

      video {
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }

    .excel-content {
      width: 100%;
      max-width: 100%;
      height: 100%;
      max-height: calc(75vh - 120px);
      display: flex;
      flex-direction: column;

      .excel-tabs {
        background-color: #f8fafc;
        padding: 0 16px;
        flex-shrink: 0;

        button {
          &:focus {
            outline: none;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
          }
        }
      }

      .excel-table-container {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0 0 8px 8px;
        flex: 1;
        min-height: 0;

        .excel-table {
          width: 100%;
          font-family: 'SF Mono', Monaco, 'Consolas', 'Liberation Mono', 'Courier New', monospace;

          td {
            background: white;
            font-size: 12px;
            line-height: 1.4;
            vertical-align: top;
            padding: 6px 8px;
            min-width: 80px;
            max-width: 150px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &:hover {
              background-color: #f3f4f6;
            }

            // Alternating row colors
            tr:nth-child(even) & {
              background-color: #f9fafb;

              &:hover {
                background-color: #f3f4f6;
              }
            }

            // Header row styling
            tr:first-child & {
              background-color: #f3f4f6;
              font-weight: 600;
              position: sticky;
              top: 0;
              z-index: 1;

              &:hover {
                background-color: #e5e7eb;
              }
            }
          }
        }
      }
    }

    .excel-fallback {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
    }

    .unsupported-preview {
      width: 100%;
    }
  }

  .file-preview-modal {
    :deep(.modal__wrapper) {
      max-height: none;
      max-width: none;

      .title {
        margin-right: 20px;
      }

      .modal__header .divider {
        max-width: none;
      }

      .modal__container {
        max-width: none;
        min-width: 800px;
        width: fit-content;
      }
    }
  }

  // Specifically target PDF modals for larger width
  .pdf-modal, .excel-modal {
    :deep(.modal__wrapper .modal__container) {
      min-width: 80vw;
      max-width: 95vw;
      width: 80vw;

      min-height: 75vh;
      max-height: 75vh;
      height: 75vh;
    }
  }
</style>
